import { compact, first } from 'lodash';
import { useMemo } from 'react';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import {
  AccountWhereInput,
  EntityStage,
  EntityWhereInput,
  InsurancePolicyPremiumPaymentSourceKind,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';
import { sortAlpha } from '@/utils/sortUtils';

import {
  InsurancePolicyDetailsPremiumPaymentSourceOptionsQueryHookResult,
  useInsurancePolicyDetailsPremiumPaymentSourceOptionsQuery,
} from '../graphql/InsurancePolicyDetailsPremiumPaymentSourceOptions.generated';

interface UseInsurancePolicyDetailsPremiumPaymentSourceOptionsOutput
  extends InsurancePolicyDetailsPremiumPaymentSourceOptionsQueryHookResult {
  options: TypeaheadSelectInputOption<string>[];
}

const HAS_DESIGNER_ACCOUNT_WITH: AccountWhereInput[] = [
  {
    hasValuationsWith: [
      {
        hasAssets: true,
      },
    ],
  },
];

export function private_getInsurancePolicyDetailsPremiumPaymentWhereInput(
  householdId: string | null
): EntityWhereInput {
  const entityWhereInput: EntityWhereInput = {
    and: [
      {
        hasHouseholdWith: [
          {
            id: householdId,
          },
        ],
      },
      {
        stage: EntityStage.Active,
      },
      {
        or: [
          {
            hasLiabilitiesLent: true,
          },
          {
            hasLiabilitiesOwed: true,
          },
          {
            hasCcorpBusinessEntityWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasCltTrustWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasCrtTrustWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasCustodialPersonalAccountWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasDonorAdvisedFundWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasGpBusinessEntityWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasGratTrustWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasIlitTrustWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasIndividualPersonalAccountWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasInsurancePersonalAccountWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasIrrevocableTrustWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasJointPersonalAccountWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasLlcBusinessEntityWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasLpBusinessEntityWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasPrivateFoundationWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasQprtTrustWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasRevocableTrustWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasScorpBusinessEntityWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasSlatTrustWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasSoleProprietorshipBusinessEntityWith: [
              {
                hasDesignerAccountWith: HAS_DESIGNER_ACCOUNT_WITH,
              },
            ],
          },
          {
            hasOwnedOwnershipStakes: true,
          },
        ],
      },
    ],
  };

  return entityWhereInput;
}

interface UseInsurancePolicyDetailsPremiumPaymentSourceOptionsInput {
  entityId?: string | undefined;
  runQuery?: boolean;
  isHypothetical?: boolean;
}

export function useInsurancePolicyDetailsPremiumPaymentSourceOptions({
  entityId = '',
  runQuery = true,
  isHypothetical = false,
}: UseInsurancePolicyDetailsPremiumPaymentSourceOptionsInput): UseInsurancePolicyDetailsPremiumPaymentSourceOptionsOutput {
  const { householdId } = useHouseholdDetailsContext();

  const entityWhereInput: EntityWhereInput =
    private_getInsurancePolicyDetailsPremiumPaymentWhereInput(householdId);

  const skipQuery = !householdId || !runQuery;
  const loadEntity = !!entityId && !skipQuery;

  const { data, ...queryProps } =
    useInsurancePolicyDetailsPremiumPaymentSourceOptionsQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        entityId,
        entityWhereInput,
        skipQuery,
        loadEntity,
      },
    });

  const options: TypeaheadSelectInputOption<string>[] = useMemo(() => {
    const responseOptions = getNodes(data?.entities)
      .filter(({ id }) => id !== entityId)
      .map<TypeaheadSelectInputOption<string>>((entity) => ({
        value: entity.id,
        display: entity.subtype.displayName,
        groupName: 'Entities',
      }))
      .sort((a, b) => sortAlpha(a.display, b.display));
    return compact([
      isHypothetical
        ? null
        : {
            value: InsurancePolicyPremiumPaymentSourceKind.Self,
            display:
              first(getNodes(data?.currentEntity))?.subtype?.displayName ??
              'Current entity',
            groupName: 'Entities',
          },
      ...responseOptions,
    ]);
  }, [data?.currentEntity, data?.entities, entityId, isHypothetical]);

  return {
    ...queryProps,
    data,
    options,
  };
}
